import React, { useState, useRef, useCallback } from 'react';
import { HOLOLAB_COLORS, HOLOLAB_FONTS } from '../../constants';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProcedureStep {
  id: number;
  label: string;
  description: string;
  actionLabel: string;
  icon: string;
}

export const PROCEDURE_STEPS: ProcedureStep[] = [
  { id: 0, label: 'Add H₂SO₄', description: 'Add 20 cm³ of 0.1 mol dm⁻³ sulphuric acid (H₂SO₄) into a conical flask.', actionLabel: 'Add H₂SO₄', icon: '⚗️' },
  { id: 1, label: 'Setup Burette', description: 'Fill a burette with water and invert it into a basin. Adjust water level to the 50 cm³ mark. Clamp vertically.', actionLabel: 'Setup Burette', icon: '🧪' },
  { id: 2, label: 'Add Zinc', description: 'Add 5 g of zinc powder (Zn) into the conical flask containing sulphuric acid.', actionLabel: 'Add Zinc Powder', icon: '⚙️' },
  { id: 3, label: 'Seal & Start', description: 'Close the flask with a rubber stopper connected to the delivery tube. Start the stopwatch.', actionLabel: 'Seal & Start Timer', icon: '▶️' },
];

interface ProcedurePanelProps {
  currentStep: number;
  isReacting: boolean;
  isPaused: boolean;
  experimentTime: number;
  buretteReading: number;
  observation: string;
  onStep: (stepId: number) => void;
  onRecordReading?: (time: number, buretteReading: number) => void;
}

// ─── Stopwatch Lap Entry ──────────────────────────────────────────────────────

interface LapEntry {
  id: number;
  time: number;        // stopwatch time in seconds
  expTime: number;     // experiment time in minutes
  buretteReading: number;
  gasVolume: number;
}

// ─── Timeline Step ─────────────────────────────────────────────────────────────

const TimelineStep: React.FC<{
  step: ProcedureStep;
  status: 'completed' | 'active' | 'locked';
  isLast: boolean;
  onAction: () => void;
}> = ({ step, status, isLast, onAction }) => {
  const isCompleted = status === 'completed';
  const isActive = status === 'active';

  return (
    <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
      {/* Timeline connector */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: '28px' }}>
        {/* Circle indicator */}
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isCompleted ? HOLOLAB_COLORS.successGreen : isActive ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.06)',
          color: isCompleted || isActive ? '#fff' : 'rgba(10,25,41,0.35)',
          fontSize: '12px', fontWeight: 700, fontFamily: HOLOLAB_FONTS.body,
          boxShadow: isActive ? '0 0 0 4px rgba(46,92,138,0.12)' : 'none',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1,
        }}>
          {isCompleted ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          ) : (
            step.id + 1
          )}
        </div>
        {/* Connecting line */}
        {!isLast && (
          <div style={{
            width: '2px', flex: 1, minHeight: '16px',
            background: isCompleted ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.08)',
            transition: 'background 300ms',
            borderRadius: '1px',
          }} />
        )}
      </div>

      {/* Content */}
      <div style={{
        flex: 1, paddingBottom: isLast ? 0 : '16px',
        opacity: status === 'locked' ? 0.4 : 1,
        transition: 'opacity 250ms',
      }}>
        {/* Label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: isActive ? '6px' : 0 }}>
          <span style={{
            fontFamily: HOLOLAB_FONTS.body, fontSize: '13px', fontWeight: 600,
            color: isActive ? HOLOLAB_COLORS.precisionBlue : isCompleted ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.5)',
            transition: 'color 250ms',
          }}>
            {step.label}
          </span>
        </div>

        {/* Description — only for active step */}
        {isActive && (
          <div style={{ marginBottom: '10px' }}>
            <p style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', color: 'rgba(10,25,41,0.65)', margin: 0, lineHeight: 1.6 }}>
              {step.description}
            </p>
          </div>
        )}

        {/* Action button — only for active */}
        {isActive && (
          <button
            onClick={onAction}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: HOLOLAB_COLORS.precisionBlue, color: '#fff',
              border: 'none', borderRadius: '8px', padding: '8px 16px',
              fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 150ms',
              boxShadow: '0 1px 4px rgba(46,92,138,0.2)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(46,92,138,0.25)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(46,92,138,0.2)'; }}
          >
            <span style={{ fontSize: '12px' }}>{step.icon}</span>
            {step.actionLabel}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Metric Card ───────────────────────────────────────────────────────────────

const Metric: React.FC<{ label: string; value: string; unit?: string; color?: string; highlight?: boolean }> = ({
  label, value, unit, color, highlight,
}) => (
  <div style={{
    display: 'flex', flexDirection: 'column', gap: '2px',
    padding: '8px 12px', borderRadius: '8px', flex: 1, minWidth: 0,
    background: highlight ? 'rgba(46,92,138,0.05)' : 'rgba(10,25,41,0.02)',
    border: highlight ? '1px solid rgba(46,92,138,0.15)' : '1px solid rgba(10,25,41,0.05)',
    transition: 'all 250ms',
  }}>
    <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '10px', color: 'rgba(10,25,41,0.45)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </span>
    <span style={{
      fontFamily: HOLOLAB_FONTS.mono, fontSize: '16px', fontWeight: 600,
      color: color || (highlight ? HOLOLAB_COLORS.precisionBlue : HOLOLAB_COLORS.deepBlue),
    }}>
      {value}
      {unit && <span style={{ fontSize: '11px', fontWeight: 400, marginLeft: '2px', opacity: 0.55 }}>{unit}</span>}
    </span>
  </div>
);

// ─── Stopwatch Component ───────────────────────────────────────────────────────

const Stopwatch: React.FC<{
  isReacting: boolean;
  isPaused: boolean;
  experimentTime: number;
  buretteReading: number;
  experimentComplete: boolean;
  onRecordReading?: (time: number, buretteReading: number) => void;
}> = ({ isReacting, isPaused, experimentTime, buretteReading, experimentComplete, onRecordReading }) => {
  const [laps, setLaps] = useState<LapEntry[]>([]);
  const lapIdRef = useRef(0);

  // Derive elapsed seconds directly from simulation time (minutes → seconds)
  const elapsed = experimentTime * 60;
  const running = isReacting && !isPaused;

  const handleRecord = useCallback(() => {
    lapIdRef.current += 1;
    const gasVol = parseFloat((50 - buretteReading).toFixed(2));
    const lap: LapEntry = {
      id: lapIdRef.current,
      time: elapsed,
      expTime: experimentTime,
      buretteReading: parseFloat(buretteReading.toFixed(2)),
      gasVolume: gasVol,
    };
    setLaps((prev) => [...prev, lap]);
    if (onRecordReading) onRecordReading(experimentTime, buretteReading);
  }, [elapsed, experimentTime, buretteReading, onRecordReading]);

  const handleReset = useCallback(() => {
    setLaps([]);
  }, []);

  // Format elapsed into mm:ss.cc
  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    const cs = Math.floor((s % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(10,25,41,0.025) 0%, rgba(46,92,138,0.04) 100%)', border: '1px solid rgba(10,25,41,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '14px' }}>⏱</span>
          <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 700, color: HOLOLAB_COLORS.deepBlue, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Stopwatch
          </span>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '12px',
          background: running ? 'rgba(16,185,129,0.1)' : experimentComplete ? 'rgba(16,185,129,0.1)' : 'rgba(10,25,41,0.05)',
        }}>
          <div style={{
            width: '5px', height: '5px', borderRadius: '50%',
            background: running ? HOLOLAB_COLORS.successGreen : experimentComplete ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.3)',
            boxShadow: running ? '0 0 6px rgba(16,185,129,0.5)' : 'none',
            animation: running ? 'pulse 1.5s infinite' : 'none',
          }} />
          <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '10px', fontWeight: 600, color: running ? HOLOLAB_COLORS.successGreen : experimentComplete ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.4)' }}>
            {experimentComplete ? 'Done' : running ? 'Running' : isPaused && elapsed > 0 ? 'Paused' : elapsed > 0 ? 'Paused' : 'Ready'}
          </span>
        </div>
      </div>

      {/* Time display */}
      <div style={{ textAlign: 'center', padding: '10px 0 6px' }}>
        <span style={{
          fontFamily: HOLOLAB_FONTS.mono, fontSize: '32px', fontWeight: 700,
          color: HOLOLAB_COLORS.deepBlue, letterSpacing: '0.02em',
          textShadow: running ? '0 0 20px rgba(46,92,138,0.15)' : 'none',
        }}>
          {formatTime(elapsed)}
        </span>
      </div>

      {/* Control buttons */}
      <div style={{ display: 'flex', gap: '6px' }}>
        {/* Record / Lap */}
        <button
          onClick={handleRecord}
          disabled={!isReacting}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            padding: '8px 0', borderRadius: '8px', border: `1.5px solid ${HOLOLAB_COLORS.successGreen}`,
            cursor: isReacting ? 'pointer' : 'default',
            fontFamily: HOLOLAB_FONTS.body, fontSize: '11px', fontWeight: 600, transition: 'all 150ms',
            background: isReacting ? 'rgba(16,185,129,0.08)' : 'transparent',
            color: isReacting ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.25)',
            opacity: isReacting ? 1 : 0.5,
          }}
          onMouseEnter={(e) => { if (isReacting) e.currentTarget.style.background = 'rgba(16,185,129,0.15)'; }}
          onMouseLeave={(e) => { if (isReacting) e.currentTarget.style.background = 'rgba(16,185,129,0.08)'; }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /></svg>
          Record
        </button>

        {/* Reset laps */}
        <button
          onClick={handleReset}
          disabled={laps.length === 0}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '36px', padding: '8px 0', borderRadius: '8px',
            border: '1px solid rgba(10,25,41,0.1)', cursor: laps.length > 0 ? 'pointer' : 'default',
            background: 'transparent', transition: 'all 150ms',
            opacity: laps.length > 0 ? 1 : 0.3,
          }}
          title="Clear recordings"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(10,25,41,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6M23 20v-6h-6" /><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" /></svg>
        </button>
      </div>

      {/* Recorded laps */}
      {laps.length > 0 && (
        <div style={{ maxHeight: '150px', overflowY: 'auto', borderRadius: '8px', border: '1px solid rgba(10,25,41,0.06)', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ background: 'rgba(10,25,41,0.02)' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left', fontFamily: HOLOLAB_FONTS.body, fontWeight: 600, color: 'rgba(10,25,41,0.5)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(10,25,41,0.06)' }}>#</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontFamily: HOLOLAB_FONTS.body, fontWeight: 600, color: 'rgba(10,25,41,0.5)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(10,25,41,0.06)' }}>Time</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontFamily: HOLOLAB_FONTS.body, fontWeight: 600, color: 'rgba(10,25,41,0.5)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(10,25,41,0.06)' }}>Exp (min)</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontFamily: HOLOLAB_FONTS.body, fontWeight: 600, color: 'rgba(10,25,41,0.5)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(10,25,41,0.06)' }}>Burette</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontFamily: HOLOLAB_FONTS.body, fontWeight: 600, color: 'rgba(10,25,41,0.5)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(10,25,41,0.06)' }}>Gas</th>
              </tr>
            </thead>
            <tbody>
              {laps.map((lap, i) => (
                <tr key={lap.id} style={{ background: i === laps.length - 1 ? 'rgba(16,185,129,0.04)' : 'transparent', animation: i === laps.length - 1 ? 'lapFlash 0.6s ease-out' : 'none' }}>
                  <td style={{ padding: '4px 8px', fontFamily: HOLOLAB_FONTS.mono, color: 'rgba(10,25,41,0.4)', borderBottom: '1px solid rgba(10,25,41,0.04)' }}>{lap.id}</td>
                  <td style={{ padding: '4px 8px', fontFamily: HOLOLAB_FONTS.mono, color: HOLOLAB_COLORS.deepBlue, textAlign: 'right', borderBottom: '1px solid rgba(10,25,41,0.04)' }}>{formatTime(lap.time)}</td>
                  <td style={{ padding: '4px 8px', fontFamily: HOLOLAB_FONTS.mono, color: HOLOLAB_COLORS.precisionBlue, textAlign: 'right', borderBottom: '1px solid rgba(10,25,41,0.04)' }}>{lap.expTime.toFixed(2)}</td>
                  <td style={{ padding: '4px 8px', fontFamily: HOLOLAB_FONTS.mono, color: HOLOLAB_COLORS.deepBlue, textAlign: 'right', borderBottom: '1px solid rgba(10,25,41,0.04)' }}>{lap.buretteReading.toFixed(2)}</td>
                  <td style={{ padding: '4px 8px', fontFamily: HOLOLAB_FONTS.mono, color: '#0A7E5C', textAlign: 'right', borderBottom: '1px solid rgba(10,25,41,0.04)' }}>{lap.gasVolume.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lap count */}
      {laps.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '10px', color: 'rgba(10,25,41,0.4)' }}>
            {laps.length} reading{laps.length !== 1 ? 's' : ''} recorded
          </span>
          <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '10px', color: 'rgba(10,25,41,0.35)' }}>
            Tip: Record every 30s
          </span>
        </div>
      )}
    </div>
  );
};

// Helper for Stopwatch — needs to be outside the component for table rendering
function formatTime(s: number) {
  const mins = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  const cs = Math.floor((s % 1) * 100);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}

// ─── Main Panel ────────────────────────────────────────────────────────────────

const ExperimentProcedure: React.FC<ProcedurePanelProps> = ({
  currentStep, isReacting, isPaused, experimentTime, buretteReading, observation, onStep, onRecordReading,
}) => {
  const getStepStatus = (stepId: number): 'completed' | 'active' | 'locked' => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'locked';
  };

  const formattedTime = experimentTime.toFixed(1);
  const formattedBurette = buretteReading.toFixed(2);
  const gasVolume = (50 - buretteReading).toFixed(2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
      {/* Header */}
      <div>
        <h2 style={{ fontFamily: HOLOLAB_FONTS.heading, fontSize: '15px', fontWeight: 700, color: HOLOLAB_COLORS.deepBlue, margin: '0 0 2px' }}>
          Procedure
        </h2>
        <p style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '11px', color: 'rgba(10,25,41,0.4)', margin: 0 }}>
          Follow each step in order
        </p>
      </div>

      {/* Timeline steps */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {PROCEDURE_STEPS.map((step, i) => (
          <TimelineStep
            key={step.id}
            step={step}
            status={getStepStatus(step.id)}
            isLast={i === PROCEDURE_STEPS.length - 1}
            onAction={() => onStep(step.id)}
          />
        ))}
      </div>

      {/* Live metrics */}
      {currentStep >= 4 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderRadius: '10px', background: 'rgba(10,25,41,0.015)', border: '1px solid rgba(10,25,41,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: isReacting ? HOLOLAB_COLORS.successGreen : experimentTime >= 5 ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.2)', boxShadow: isReacting ? '0 0 6px rgba(16,185,129,0.4)' : 'none' }} />
            <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '11px', fontWeight: 600, color: isReacting ? HOLOLAB_COLORS.successGreen : experimentTime >= 5 ? HOLOLAB_COLORS.successGreen : 'rgba(10,25,41,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {experimentTime >= 5.0 ? 'Complete' : isReacting ? 'Reacting' : 'Ready'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Metric label="Time" value={isReacting || experimentTime > 0 ? formattedTime : '—'} unit="min" highlight={isReacting} />
            <Metric label="Burette" value={isReacting || experimentTime > 0 ? formattedBurette : '50.00'} unit="cm³" highlight={isReacting} />
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Metric label="Gas Vol" value={isReacting || experimentTime > 0 ? gasVolume : '0.00'} unit="cm³" color="#0A7E5C" />
            <Metric
              label="Progress"
              value={experimentTime >= 5.0 ? '100' : Math.round((experimentTime / 5) * 100).toString()}
              unit="%"
            />
          </div>
        </div>
      )}

      {/* Stopwatch with record feature */}
      {currentStep >= 4 && (
        <Stopwatch
          isReacting={isReacting}
          isPaused={isPaused}
          experimentTime={experimentTime}
          buretteReading={buretteReading}
          experimentComplete={experimentTime >= 5.0}
          onRecordReading={onRecordReading}
        />
      )}

      {/* Hint for recording */}
      {isReacting && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)' }}>
          <span style={{ fontSize: '12px' }}>📋</span>
          <p style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '11px', color: 'rgba(10,25,41,0.65)', margin: 0, lineHeight: 1.5 }}>
            Press <strong>Record</strong> to capture the current burette reading at this time.
          </p>
        </div>
      )}

      {/* Lap flash animation */}
      <style>{`
        @keyframes lapFlash {
          0% { background: rgba(16,185,129,0.15); }
          100% { background: rgba(16,185,129,0.04); }
        }
      `}</style>
    </div>
  );
};

export default ExperimentProcedure;
